/* eslint-disable react/prop-types */
import useTaskView from '../../../MyTask/hooks/useTaskView';
import TaskViewProvider from '../../../MyTask/table/TaskViewProvider';
import TaskStatusProvider, { TaskPriorityProvider } from '../../../MyTask/table/TaskStatusProvider';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import saveAs from 'file-saver';

import { useLocalStorage } from 'react-use';

import {
  EditingState,
  PagingState,
  IntegratedPaging,
  SortingState,
  IntegratedSorting,
  SearchState,
  IntegratedFiltering,
  SelectionState,
  IntegratedSelection
} from '@devexpress/dx-react-grid';
import { GridExporter } from '@devexpress/dx-react-grid-export';

import {
  Grid,
  Table,
  TableHeaderRow,
  PagingPanel,
  TableColumnResizing,
  DragDropProvider,
  TableColumnReordering,
  SearchPanel,
  Toolbar,
  TableSelection,
  ColumnChooser,
  TableColumnVisibility,
  ExportPanel,
  TableInlineCellEditing,
  TableEditColumn
} from '@devexpress/dx-react-grid-material-ui';

import Paper from '@mui/material/Paper';
import { toast } from 'react-hot-toast';

import HeaderFilterCell, { HeaderFilterContext } from './customTemplate/HeaderFilterCell';
import { getRowCellValue } from './customTemplate/HeaderFilterCell';

import { Popup, PopupEditing } from './TotalTaskPopupEditing';
import {
  CalendarProvider,
  TotalTaskDependenciesProvider,
  TotalTaskFocusableCell,
  TotalTaskLookupProvider,
  TotalTaskSupportingStaffProvider,
  TotalTaskTextProvider
} from './TotalTaskTableInlineEditing';
import useFetchActiveEmployees from '../hooks/useFectchActiveEmployees';
import taskService from '../../../../services/TaskService';
import { mapTaskToRow } from '../../../MyTask/helper/helper';
import KebabCommandCell from './customTemplate/KebabCommandCell';
import { TaskToolbarContext, TaskToolbarRoot } from '../../../MyTask/table/customTemplate/CustomToolbarRoot';
import useTaskReminders from '../../../MyTask/hooks/useTaskReminders';

// ---------------------------------------------------------------------------
// Unique row ID – MongoDB uses _id
// ---------------------------------------------------------------------------
const getRowId = (row) => row.id || row._id;

// ---------------------------------------------------------------------------
// Clicking a data row opens its task details. Interactive controls such as the
// selection checkbox keep their own behaviour.
// ---------------------------------------------------------------------------
const ClickableTaskRow = ({ row, onOpen, ...restProps }) => {
  const handleDoubleClick = (event) => {
    if (
      !row ||
      event.target.closest('button, a, input[type="checkbox"], [role="checkbox"], [role="button"], [role="menuitem"]')
    )
      return;
    onOpen(getRowId(row));
  };

  const handleKeyDown = (event) => {
    if (event.currentTarget !== event.target) return;
    if (!row || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    onOpen(getRowId(row));
  };

  return (
    <Table.Row
      {...restProps}
      row={row}
      tabIndex={row ? 0 : undefined}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      aria-label={row ? `Open ${row.title || 'task'} details` : undefined}
      style={{
        ...restProps.style,
        cursor: row ? 'pointer' : restProps.style?.cursor
      }}
    />
  );
};

// ---------------------------------------------------------------------------
// Export helper
// ---------------------------------------------------------------------------
const onSave = (workbook) => {
  workbook.xlsx.writeBuffer().then((buffer) => {
    // eslint-disable-next-line no-undef
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Tasks.xlsx');
  });
};

// ---------------------------------------------------------------------------
// Column definitions per tab
// ---------------------------------------------------------------------------

const TOTAL_TASK_COLUMNS = [
  { name: 'title', title: 'Task Title' },
  { name: 'projectName', title: 'Project' },
  { name: 'type', title: 'Type' },
  { name: 'class', title: 'Class' },
  { name: 'assignedTo', title: 'Assigned To' },
  { name: 'taskViewedByAssignee', title: 'Task Viewed By Assignee' },
  { name: 'taskViewedBySupporter', title: 'Task Viewed By Supporter' },
  { name: 'status', title: 'Status' },
  { name: 'priority', title: 'Priority' },
  { name: 'createdDate', title: 'Created Date' },
  { name: 'dueDate', title: 'Due Date' },
  { name: 'startTime', title: 'Start Time' },
  { name: 'dependencies', title: 'Dependencies' },
  { name: 'createdBy', title: 'Created By' },
  { name: 'pendingReason', title: 'Pending Reason' },
  { name: 'description', title: 'Description' },
  { name: 'pendingComment', title: 'Pending Comment' },
  { name: 'supportingStaff', title: 'Supporting Staff' },
  { name: 'location', title: 'Location' },
  { name: 'estimatedHours', title: 'Estimated Hours' },
  { name: 'actualHours', title: 'Actual Hours' },
  { name: 'submittedDate', title: 'Submitted Date' },
  { name: 'approvedDate', title: 'Approved Date' },
  { name: 'updatedAt', title: 'Last Updated' }
];

const DEFAULT_COL_WIDTH = 160;
const buildWidths = (cols) => cols.map(({ name }) => ({ columnName: name, width: DEFAULT_COL_WIDTH }));
const buildOrder = (cols) => cols.map(({ name }) => name);
const TOTAL_TASK_INLINE_COLUMNS = [
  'type',
  'class',
  'assignedTo',
  'status',
  'priority',
  'dueDate',
  'dependencies',
  'description',
  'supportingStaff',
  'location',
  'estimatedHours',
  'actualHours'
];

const normaliseTotalTaskRow = (row) => ({
  ...row,
  dueDate: row?._raw?.dueDate || row?.dueDate || '',
  startTime: row?._raw?.startTime || row?.startTime || ''
});

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function TotalTaskTable({
  rows,
  setRows,
  selection,
  setSelection,
  loading,
  classRecords = [],
  onAddTask
}) {
  const [columnWidths, setColumnWidths] = useLocalStorage('totalTaskColumnWidthsV3', buildWidths(TOTAL_TASK_COLUMNS));
  const [columnOrder, setColumnOrder] = useLocalStorage('totalTaskColumnOrderV3', buildOrder(TOTAL_TASK_COLUMNS));
  const validColumnNames = useMemo(() => TOTAL_TASK_COLUMNS.map(({ name }) => name), []);
  const resolvedColumnWidths = useMemo(() => {
    const savedWidths = (Array.isArray(columnWidths) ? columnWidths : []).filter(
      ({ columnName, width }) =>
        validColumnNames.includes(columnName) && Number.isFinite(Number(width)) && Number(width) > 0
    );

    return TOTAL_TASK_COLUMNS.map(({ name }) => ({
      columnName: name,
      width: Number(savedWidths.find(({ columnName }) => columnName === name)?.width) || DEFAULT_COL_WIDTH
    }));
  }, [columnWidths, validColumnNames]);
  const resolvedColumnOrder = useMemo(() => {
    const savedOrder = (Array.isArray(columnOrder) ? columnOrder : []).filter(
      (name, index, order) => validColumnNames.includes(name) && order.indexOf(name) === index
    );
    const missingColumns = TOTAL_TASK_COLUMNS.map(({ name }) => name).filter((name) => !savedOrder.includes(name));
    return [...savedOrder, ...missingColumns];
  }, [columnOrder, validColumnNames]);

  const [sorting, setSorting] = useState([{ columnName: 'dueDate', direction: 'asc' }]);
  const [hiddenColumnNames, setHiddenColumnNames] = useLocalStorage('totalTaskHiddenColumnNames', []);

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [pageSizes] = useState([25, 50, 100]);
  const [searchValue, setSearchState] = useState('');

  const [columnFilters, setColumnFilters] = useLocalStorage('totalTaskColumnFilter', {});

  const [taskTabIndex, setTaskTabIndex] = useState(0);
  const [editingCellIds, setEditingCellIds] = useState([]);
  const [editingRowIds, setEditingRowIds] = useState([]);
  useTaskView(editingRowIds[0], setRows);
  const openTaskDetails = useCallback((rowId) => {
    setEditingCellIds([]);
    setEditingRowIds([rowId]);
  }, []);
  const startPopupEditing = useCallback(() => {
    setEditingCellIds([]);
    setTaskTabIndex(0);
  }, []);
  const [validationStatus, setValidationStatus] = useState({});

  const { activeEmployeeOptions } = useFetchActiveEmployees();
  const classOptions = useMemo(
    () => classRecords.map((c) => ({ value: c.value, label: c.name, id: c.value })),
    [classRecords]
  );

  const [lookupColumns] = useState(['type', 'class', 'assignedTo', 'status', 'priority']);
  const [calendarColumn] = useState(['dueDate']);

  const editingStateColumnExtensions = useMemo(
    () =>
      TOTAL_TASK_COLUMNS.map(({ name }) => ({
        columnName: name,
        editingEnabled: TOTAL_TASK_INLINE_COLUMNS.includes(name)
      })),
    []
  );

  const dependencyOptions = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return rows
      .filter((row) => {
        const dueDate = new Date(row?._raw?.dueDate || row.dueDate);
        return !Number.isNaN(dueDate.getTime()) && dueDate >= today;
      })
      .map((row) => ({
        value: getRowId(row),
        label: row.title || `Task ${String(getRowId(row)).slice(-8)}`
      }));
  }, [rows]);

  const commitChanges = useCallback(
    async ({ changed, deleted }) => {
      if (deleted?.length) {
        // eslint-disable-next-line no-undef
        if (!window.confirm('Are you sure you want to delete this task?')) return;
        try {
          // eslint-disable-next-line no-undef
          await Promise.all(deleted.map((rowId) => taskService.deleteTask(rowId)));
          const deletedIds = deleted.map(String);
          setRows((currentRows) => currentRows.filter((row) => !deletedIds.includes(String(getRowId(row)))));
          toast.success('Task deleted');
        } catch (error) {
          toast.error(error?.response?.data?.message || 'Could not delete task');
        }
        return;
      }

      const entries = Object.entries(changed || {}).filter(
        ([, rowChanges]) => rowChanges && Object.keys(rowChanges).length
      );
      if (!entries.length) return;

      const previousRows = rows;
      try {
        // eslint-disable-next-line no-undef
        const updatedRows = await Promise.all(
          entries.map(async ([rowId, rowChanges]) => {
            const changes = { ...rowChanges };
            const currentRow = rows.find((row) => String(row.id) === String(rowId));
            if (changes.status === 'Draft' && !Object.prototype.hasOwnProperty.call(changes, 'assignedTo')) {
              changes.assignedTo = [];
            }
            if (Object.prototype.hasOwnProperty.call(changes, 'assignedTo')) {
              const selectedAssigneeId = changes.assignedTo;
              changes.assignedTo = changes.assignedTo
                ? [{ employeeId: changes.assignedTo, assignedDate: new Date().toISOString() }]
                : [];
              if (!Object.prototype.hasOwnProperty.call(changes, 'status')) {
                if (selectedAssigneeId && currentRow?.status === 'Draft') {
                  changes.status = 'Assigned';
                } else if (!selectedAssigneeId && currentRow?.status === 'Assigned') {
                  changes.status = 'Draft';
                }
              }
            }
            if (Object.prototype.hasOwnProperty.call(changes, 'class')) {
              const selectedClass = classOptions.find((option) => String(option.value) === String(changes.class));
              changes.class = selectedClass ? { id: selectedClass.value, name: selectedClass.label } : null;
            }
            if (Object.prototype.hasOwnProperty.call(changes, 'supportingStaff')) {
              changes.supportingStaff = (changes.supportingStaff || []).map((employeeId) => ({ employeeId }));
            }
            if (Object.prototype.hasOwnProperty.call(changes, 'estimatedHours')) {
              const estimatedHours = changes.estimatedHours;
              if (estimatedHours === '') changes.estimatedHours = null;
              else if (!Number.isFinite(Number(estimatedHours)) || Number(estimatedHours) < 0) {
                throw new Error('Estimated hours must be zero or greater');
              } else changes.estimatedHours = Number(estimatedHours);
            }
            if (Object.prototype.hasOwnProperty.call(changes, 'actualHours')) {
              if (currentRow?.status !== 'Complete') {
                throw new Error('Actual hours can only be edited for a completed task');
              }
              if (
                changes.actualHours === '' ||
                !Number.isFinite(Number(changes.actualHours)) ||
                Number(changes.actualHours) < 0
              ) {
                throw new Error('Actual hours must be zero or greater');
              }
              changes.actualHours = Number(changes.actualHours);
            }
            const response = await taskService.updateTask(rowId, changes, 'totalTask');
            return normaliseTotalTaskRow(mapTaskToRow(response?.data?.data));
          })
        );

        setRows((currentRows) => currentRows.map((row) => updatedRows.find((updated) => updated.id === row.id) || row));
        setValidationStatus({});
        toast.success('Task updated');
      } catch (error) {
        setRows(previousRows);
        toast.error(error?.response?.data?.message || error?.message || 'Could not update task');
      }
    },
    [classOptions, rows, setRows]
  );

  const onUpdateRow = useCallback(
    (updatedRow) => {
      const normalisedRow = normaliseTotalTaskRow(updatedRow);
      setRows((prevRows) => prevRows.map((r) => (r.id === normalisedRow.id ? { ...r, ...normalisedRow } : r)));
    },
    [setRows]
  );

  // ── Export ─────────────────────────────────────────────────────────────────
  const exporterRef = useRef(null);
  const startExport = useCallback((options) => exporterRef.current.exportGrid(options), [exporterRef]);

  // ── Header-filter rows ─────────────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      for (const columnName of Object.keys(columnFilters)) {
        const activeVals = columnFilters[columnName];
        if (activeVals && activeVals.length > 0) {
          const rowVal = getRowCellValue(row, columnName);
          if (!activeVals.includes(rowVal)) return false;
        }
      }
      return true;
    });
  }, [rows, columnFilters]);

  const { isSendingReminders, sendAllReminders, sendSelectedReminders } = useTaskReminders({
    filteredRows,
    selection,
    taskSource: 'totalTask'
  });

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '3rem', color: '#9e9e9e' }}>Loading tasks...</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: '4px', fontSize: '0.85rem', color: '#777' }}>
        {filteredRows.length} task{filteredRows.length !== 1 ? 's' : ''}
        {selection.length > 0 && ` · ${selection.length} selected`}
      </div>

      <TaskToolbarContext.Provider
        value={{
          showAddTask: true,
          showSend: true,
          selectionCount: selection.length,
          isSending: isSendingReminders,
          isAddTaskDisabled: loading,
          onAddTask,
          onSendAll: sendAllReminders,
          onSendSelected: sendSelectedReminders
        }}
      >
        <HeaderFilterContext.Provider value={{ rows, columnFilters, onFilterChange: setColumnFilters }}>
          <Paper>
            <Grid rows={filteredRows} columns={TOTAL_TASK_COLUMNS} getRowId={getRowId}>
              <EditingState
                editingCellIds={editingCellIds}
                onEditingCellIdsChange={(cellIds) => {
                  if (editingRowIds.length) return;
                  setEditingCellIds(
                    cellIds.filter(({ rowId, columnName }) => {
                      if (!TOTAL_TASK_INLINE_COLUMNS.includes(columnName)) return false;
                      if (columnName !== 'actualHours') return true;
                      const task = rows.find((row) => String(getRowId(row)) === String(rowId));
                      return task?.status === 'Complete';
                    })
                  );
                }}
                editingRowIds={editingRowIds}
                onEditingRowIdsChange={(rowIds) => {
                  setEditingRowIds(rowIds);
                  if (rowIds.length) setEditingCellIds([]);
                }}
                onCommitChanges={commitChanges}
                columnExtensions={editingStateColumnExtensions}
              />

              <CalendarProvider for={calendarColumn} />
              <TotalTaskLookupProvider
                for={lookupColumns}
                optionsMap={{
                  assignedTo: activeEmployeeOptions,
                  class: classOptions
                }}
              />
              <TotalTaskDependenciesProvider for={['dependencies']} options={dependencyOptions} />
              <TotalTaskSupportingStaffProvider for={['supportingStaff']} options={activeEmployeeOptions} />
              <TotalTaskTextProvider for={['description', 'location', 'estimatedHours', 'actualHours']} />

              <TaskViewProvider />
              <TaskStatusProvider />
              <TaskPriorityProvider />

              <PagingState
                currentPage={currentPage}
                onCurrentPageChange={setCurrentPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
              />
              <SelectionState selection={selection} onSelectionChange={setSelection} />
              <IntegratedSelection />
              <DragDropProvider />
              <SortingState sorting={sorting} onSortingChange={setSorting} />
              <IntegratedSorting />
              <SearchState value={searchValue} onValueChange={setSearchState} />
              <IntegratedFiltering />
              <IntegratedPaging />

              <Table
                rowComponent={(props) => <ClickableTaskRow {...props} onOpen={openTaskDetails} />}
                cellComponent={(props) => (
                  <TotalTaskFocusableCell
                    {...props}
                    validationStatus={validationStatus}
                    setValidationStatus={setValidationStatus}
                  />
                )}
              />

              <TableColumnResizing columnWidths={resolvedColumnWidths} onColumnWidthsChange={setColumnWidths} />
              <TableColumnReordering order={resolvedColumnOrder} onOrderChange={setColumnOrder} />
              <TableHeaderRow showSortingControls cellComponent={HeaderFilterCell} />
              <TableSelection showSelectAll />
              <PagingPanel pageSizes={pageSizes} />

              {/* Native grid edit/delete commands, displayed through the shared UserTable kebab cell. */}
              <TableEditColumn
                width={90}
                showEditCommand
                showDeleteCommand
                cellComponent={(props) => <KebabCommandCell {...props} onPopupEditStart={startPopupEditing} />}
              />

              <TableInlineCellEditing startEditAction="click" selectTextOnEditStart />
              <PopupEditing
                popupComponent={Popup}
                taskTabIndex={taskTabIndex}
                setTaskTabIndex={setTaskTabIndex}
                onUpdateRow={onUpdateRow}
                classOptions={classOptions}
                dependencyOptions={dependencyOptions}
              />
              <TableColumnVisibility
                hiddenColumnNames={hiddenColumnNames}
                onHiddenColumnNamesChange={setHiddenColumnNames}
              />
              <Toolbar rootComponent={TaskToolbarRoot} />
              <SearchPanel />
              <ColumnChooser />
              <ExportPanel startExport={startExport} />
            </Grid>

            <GridExporter
              ref={exporterRef}
              columns={TOTAL_TASK_COLUMNS}
              rows={filteredRows}
              selection={selection}
              onSave={onSave}
              getRowId={getRowId}
              hiddenColumnNames={hiddenColumnNames}
            />
          </Paper>
        </HeaderFilterContext.Provider>
      </TaskToolbarContext.Provider>
    </div>
  );
}
